/**
 * Putting a thing together, one part at a time.
 *
 * The rule a child meets: pick a bone up, hold it roughly where it belongs,
 * let go, and it clicks into place. "Roughly" is the design, not a
 * concession -- a ten-year-old pinching in mid-air is not accurate to a
 * centimetre, and a lesson about the skeleton must not turn into a test of
 * fine motor control.
 *
 * No three.js in here on purpose. This is the part that decides whether a
 * student got it right, so it has to be testable without a headset -- the
 * same split as the lesson player and the session state machine.
 *
 * Where a part belongs is its `pos` in the lesson's `objects` (ADR-025): the
 * lesson document states the finished thing, and the engine scatters it. One
 * source of truth, so there is no second copy of the right answer to keep in
 * step with the first.
 */

export type Vec3 = readonly [number, number, number];

export type AssemblyPart = {
  id: string;
  /** Where this part belongs: its `pos` in the lesson document. */
  home: Vec3;
};

/**
 * What happened when a part was let go.
 *
 * `wrong-place` is not an error and must never be presented as one -- the
 * part goes back to the pile and the child tries again.
 */
export type PlaceOutcome = 'placed' | 'wrong-place' | 'unknown';

/** What the teacher gets back. About the thing built, never about the child. */
export type AssemblyResult = {
  parts: number;
  placed: number;
  /** How many times a part was let go somewhere it did not belong. */
  misplaced: number;
  /**
   * The part that was got wrong most often, when any was.
   *
   * The useful half of the result: "the ribs were hard this year" is a thing
   * a teacher can teach differently next time, and it names no child
   * (ADR-002).
   */
  hardest?: string;
};

/** How close counts as placed, when the lesson does not say. */
export const DEFAULT_SNAP_METRES = 0.08;

function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

export class Assembly {
  readonly #parts: Map<string, AssemblyPart>;
  readonly #snap: number;
  readonly #placed = new Set<string>();
  readonly #misses = new Map<string, number>();

  constructor(options: { parts: readonly AssemblyPart[]; snapMetres?: number }) {
    this.#parts = new Map(options.parts.map((part) => [part.id, part]));
    this.#snap = options.snapMetres ?? DEFAULT_SNAP_METRES;
  }

  /**
   * Lets a part go at a position.
   *
   * A part is judged against its own home and nothing else. The mistake worth
   * guarding against is a child holding the tibia over the skull's place: it
   * is near *a* home, just not its own, and snapping it in would teach the
   * wrong thing -- the one outcome worse than not snapping at all.
   */
  place(id: string, at: Vec3): PlaceOutcome {
    const part = this.#parts.get(id);
    if (!part) return 'unknown';

    if (distance(at, part.home) > this.#snap) {
      this.#misses.set(id, (this.#misses.get(id) ?? 0) + 1);
      return 'wrong-place';
    }

    this.#placed.add(id);
    return 'placed';
  }

  /**
   * Takes a placed part back out.
   *
   * A child who wants to pick something up and look at it should not be
   * punished for the curiosity a lesson is trying to provoke.
   */
  take(id: string): void {
    this.#placed.delete(id);
  }

  isPlaced(id: string): boolean {
    return this.#placed.has(id);
  }

  get remaining(): number {
    return this.#parts.size - this.#placed.size;
  }

  get done(): boolean {
    return this.remaining === 0;
  }

  result(): AssemblyResult {
    let hardest: string | undefined;
    let worst = 0;
    for (const [id, misses] of this.#misses) {
      if (misses > worst) {
        worst = misses;
        hardest = id;
      }
    }

    let misplaced = 0;
    for (const misses of this.#misses.values()) misplaced += misses;

    return {
      parts: this.#parts.size,
      placed: this.#placed.size,
      misplaced,
      ...(hardest === undefined ? {} : { hardest }),
    };
  }
}
